/* eslint-disable no-nested-ternary */
import handleChatWidgetOpen from "helpers/handleChatWidgetOpen";
import classes from "./ZendeskChat.module.scss";
import styles from "./ZendeskChatChina.module.scss";
import { useEffect, CSSProperties, useState, useRef } from "react";
import LoaderIcon from "@Icons/LoaderIcon";
// import ChatColor from "@Icons/ChatColor";
import ZendeskChatIcon from "@Icons/ZendeskChatIcon";
import { closeLeadFormModal } from "@Components/LeadFormModal/data/leadFormReducer";
import loadSwiftsellScript from "helpers/loadSwiftsellScript";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "@Store/index";
import CheveronDown from "@Icons/ChevronDown";
import getSwiftsellActiveTimeRange from "helpers/getSwiftsellActiveTimeRange";
import { useFeatureIsOn, useFeatureValue } from "@growthbook/growthbook-react";
import { GROWTH_BOOK_FEATURE_FLAG_NAMES } from "constants/index";
import handleLoadChatWidget from "helpers/handleLoadChatWidget";
import {
  toggleLoaderState,
  toggleLoadZendeskAfterLogin,
  toggleShowWidgetState,
  setOpenAmberCopilot,
} from "./ChatBotReducer";
import getParsedLocalStorageData from "@Components/LeadFormModal/helpers/getParsedLocalStorageData";
import LoginDispatcher from "@Components/Login/actions";
import {
  addLoadZendeskParamInURL,
  removeLoadZendeskParamFromURL,
} from "@Pages/PropertyPage/data/helper";
import wrapperHOC from "@Utils/wrapperHOC";
import config from "@Config/index";
import Image from "@Components/Image";
import Button from "@Components/Button";
import isMiniProgramUserAgent from "@Utils/isMiniProgramUserAgent";
import useIsDesktop from "hooks/useIsDesktop";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import useIsTabletPortrait from "hooks/useIsTabletPortrait";
import useIsMiniProgram from "hooks/useIsMiniProgram";
import emitAiChatInitiatedEvent from "helpers/emitAiChatInitiatedEvent";

type ZendeskChatProps = {
  isVisibleOnPhone?: boolean;
};
interface CustomCSSProperties extends CSSProperties {
  "--bottom-position"?: string;
}

const ZendeskChat = ({ isVisibleOnPhone = false }: ZendeskChatProps) => {
  const {
    isLFOneFilled,
    personalDetails,
    propertyName,
    region,
    showWidget,
    loader,
    loadZendeskAfterLogin,
    inModal,
    isUserLoggedIn,
  } = useSelector(
    ({ leadFormModal, searchPageData, propertyPageData, chatBotReducer, appData }: AppState) => ({
      isLFOneFilled: leadFormModal.isLFOneFilled,
      personalDetails: leadFormModal.personalDetails,
      propertyName: propertyPageData.data?.name,
      region: searchPageData.region,
      loader: chatBotReducer.loader,
      showWidget: chatBotReducer.showWidget,
      loadZendeskAfterLogin: chatBotReducer.loadZendeskAfterLoad,
      inModal: leadFormModal.inModal,
      isUserLoggedIn: appData.isUserLoggedIn,
    }),
  );

  const bottomPaddingStyles: CustomCSSProperties = {
    // eslint-disable-next-line no-nested-ternary
    "--bottom-position": isMiniProgramUserAgent() ? "100px" : isVisibleOnPhone ? "70px" : "16px",
  };

  const imageSrcRef = useRef("bot.gif");
  const chatBotRef = useRef(null);
  const isDesktop = useIsDesktop();
  const isMiniProgram = useIsMiniProgram();
  const [isExpanded, setIsExpanded] = useState(isDesktop);
  const dispatch = useDispatch();
  const isSwiftsellEnabled = false;
  const isZendeskAfterLogin = useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.ZENDESK_LOGIN_POPUP);
  const amberCopilotFeatureValue = useFeatureValue(
    GROWTH_BOOK_FEATURE_FLAG_NAMES.AMBER_COPILOT,
    "pending",
  ) as any;
  const isAmberCopilotEnabled: boolean | "pending" = config.IS_CHINA_APP
    ? false
    : amberCopilotFeatureValue;
  const isTabletPortrait = useIsTabletPortrait();
  const handleOnZendeskClick = () => {
    if (config.IS_CHINA_APP) {
      // @ts-ignore
      if (typeof _MEIQIA !== "undefined" && _MEIQIA)
        // @ts-ignore
        _MEIQIA("showPanel");
      gtagEventEmitter({
        action: "meqia_chatbot_clicked_cn",
        category: "zendesk_icon_china",
      });
      if (chatBotRef.current) {
        (chatBotRef.current as HTMLElement).style.display = "none";
      }
      return;
    }
    if (isAmberCopilotEnabled) {
      dispatch(setOpenAmberCopilot(true));
      emitAiChatInitiatedEvent();
      return;
    }
    if (isZendeskAfterLogin && !isUserLoggedIn && !isLFOneFilled) {
      addLoadZendeskParamInURL();
      const loginDispatcher = new LoginDispatcher(dispatch);
      loginDispatcher.showLoginModal({ openingSource: "zendesk" });
    } else {
      handleChatWidgetOpen(false);
      dispatch(toggleLoaderState(true));
      setTimeout(() => {
        if (isDesktop || isTabletPortrait) dispatch(toggleShowWidgetState(true));
        dispatch(toggleLoaderState(false));
      }, 2000);
    }
  };

  const closeChatWidget = () => {
    if (chatBotRef.current) {
      (chatBotRef.current as HTMLElement).style.display = "flex";
    }
  };

  useEffect(() => {
    if (isAmberCopilotEnabled) return;
    const timeoutId = setTimeout(() => {
      imageSrcRef.current = "bot-image.png";
    }, 3000);
    // eslint-disable-next-line no-unused-expressions
    () => {
      clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    // @ts-ignore
    if (typeof _MEIQIA !== "undefined" && _MEIQIA) {
      // @ts-ignore
      _MEIQIA("closeSmartGuide", closeChatWidget);
      // @ts-ignore
      _MEIQIA("beforeCloseWindow", closeChatWidget);
    }
  }, [chatBotRef.current]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsExpanded(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    if (isAmberCopilotEnabled === "pending" || isAmberCopilotEnabled) {
      return undefined;
    }
    const delay = isZendeskAfterLogin && loadZendeskAfterLogin ? 0 : 10000;
    const timeoutId = setTimeout(() => {
      if (isZendeskAfterLogin && loadZendeskAfterLogin) {
        removeLoadZendeskParamFromURL();
        dispatch(toggleLoadZendeskAfterLogin(false));
        handleChatWidgetOpen(false);
        dispatch(toggleShowWidgetState(true));
      } else handleLoadChatWidget(false);
    }, delay);
    return () => clearTimeout(timeoutId);
  }, [isZendeskAfterLogin, isAmberCopilotEnabled]);

  const handleSwiftsellClick = () => {
    if (inModal) dispatch(closeLeadFormModal());
    const leadSavedData = getParsedLocalStorageData("lead-form-one-success-data");

    const { fullName, email, code, mobileNumber } = personalDetails;
    const dataPayload = {
      name: fullName,
      email,
      phone: `${code}${mobileNumber}`,
      ...(propertyName && { propertyName }),
      ...(region && region?.location?.name && { cityName: region.location.name }),
      // @ts-ignore
      ...(leadSavedData && leadSavedData.id && { leadId: leadSavedData.id }),
    };

    loadSwiftsellScript(dataPayload);
    dispatch(toggleLoaderState(true));
    setTimeout(() => {
      dispatch(toggleShowWidgetState(true));
      dispatch(toggleLoaderState(false));
    }, 1000);
  };

  const handleCloseWidget = () => {
    dispatch(toggleShowWidgetState(false));
    // @ts-ignore
    if (typeof zE === "function") {
      // @ts-ignore
      zE("messenger", "close");
    }
    // @ts-ignore
    if (isSwiftsellEnabled && typeof wn !== "undefined" && typeof wn.hideWidget === "function") {
      // @ts-ignore
      wn.hideWidget();
    }
  };

  const handleWidgetClick = () =>
    isSwiftsellEnabled && isLFOneFilled && !getSwiftsellActiveTimeRange()
      ? handleSwiftsellClick()
      : handleOnZendeskClick();

  const WidgetIcon = () =>
    showWidget ? (
      <div
        className={classes.container}
        role="button"
        onClick={handleCloseWidget}
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            handleCloseWidget();
          }
          if (event.keyCode === 27) {
            // Esc
            handleCloseWidget();
          }
        }}
        aria-label="close-widget"
      >
        <div className={classes.closeIcon}>
          <CheveronDown />
        </div>
      </div>
    ) : (
      <div
        className={classes.container}
        role="button"
        tabIndex={0}
        onClick={() => handleWidgetClick()}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            if (isSwiftsellEnabled && isLFOneFilled && !getSwiftsellActiveTimeRange()) {
              handleSwiftsellClick();
            } else {
              handleOnZendeskClick();
            }
          }
          if (event.keyCode === 27) {
            // Esc
            handleCloseWidget();
          }
        }}
        aria-label="chatbot"
      >
        <ZendeskChatIcon />
      </div>
    );

  const ChinaWidgetIcon = () =>
    showWidget ? (
      <div
        ref={chatBotRef}
        className={styles.chinaLoadingcontainer}
        role="button"
        style={bottomPaddingStyles}
        onClick={handleCloseWidget}
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            handleCloseWidget();
          }
          if (event.keyCode === 27) {
            // Esc
            handleCloseWidget();
          }
        }}
        aria-label="close-widget"
      >
        <div className={styles.closeIcon}>
          <CheveronDown />
        </div>
      </div>
    ) : isMiniProgram ? (
      <></>
    ) : (
      <div
        ref={chatBotRef}
        className={isExpanded ? styles.expandedBotContainer : styles.botContainer}
        role="button"
        style={bottomPaddingStyles}
        tabIndex={0}
        onClick={() => handleOnZendeskClick()}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            handleOnZendeskClick();
          }
          if (event.keyCode === 27) {
            // Esc
            handleCloseWidget();
          }
        }}
        aria-label="chatbot"
      >
        <Image
          src={`${config?.IMAGE_STATIC_URL}/images/${imageSrcRef.current}`}
          width={64}
          height={64}
        />
        {isDesktop && (
          <div className={isExpanded ? styles.expandedHiddenContainer : styles.hiddenContainer}>
            <div className={styles.divider} />
            <div className={styles.subContainer}>
              <p className={styles.heading}>我能为你做什么？</p>
              <Button
                onClick={() => handleOnZendeskClick()}
                variant="none"
                className={styles.chatNowButton}
                size="sm"
              >
                立即聊天
              </Button>
            </div>
          </div>
        )}
      </div>
    );

  if (isAmberCopilotEnabled && !config?.IS_CHINA_APP) {
    return <></>;
  }
  return (
    <>
      {loader ? (
        <div
          style={bottomPaddingStyles}
          className={config?.IS_CHINA_APP ? styles.chinaLoadingcontainer : classes.container}
        >
          <LoaderIcon fill="#fff" />
        </div>
      ) : config?.IS_CHINA_APP ? (
        <ChinaWidgetIcon />
      ) : (
        <WidgetIcon />
      )}
    </>
  );
};
export default wrapperHOC(ZendeskChat, {
  componentName: "ZendeskChat-PropertyPage",
  showForChina: true,
  showForWhiteLable: false,
});
